
import {
  defineComponent,
  reactive,
  toRefs,
  computed,
  onMounted,
  Ref
} from "vue";
import { formatDateTime } from "../../modules/utils";
import { useConfirm, useToast } from "../../modules/message";
import { useRntrcFilaService } from "../../modules/business/rntrc/rntrcFilaBusiness";
import {
  RntrcFilaPainelPayload,
  formatCpfCnpj,
  getSituacaoCor,
  getSituacaoLabel,
  getTipoServicoLabel
} from "../../modules/business/rntrc/rntrcTypes";

interface FilaRntrcPainelState {
  linhas: Array<RntrcFilaPainelPayload>;
  loadings: Array<Ref<boolean>>;
}

export default defineComponent({
  setup() {
    const state = reactive<FilaRntrcPainelState>({
      linhas: [],
      loadings: []
    });

    const stateLoading = computed(() => state.loadings.length !== 0);

    const removeLoading = (loading: Ref<boolean>) => {
      const idx = state.loadings.indexOf(loading);
      if (idx >= 0) {
        state.loadings.splice(idx, 1);
      }
    };

    const submitSearch = () => {
      const { data, loading, listar } = useRntrcFilaService();
      state.loadings.push(loading);
      return listar()
        .then(() => {
          state.linhas = Array.isArray(data.value) ? data.value : [];
          removeLoading(loading);
        })
        .catch(() => removeLoading(loading));
    };

    const descricaoLinha = (l: RntrcFilaPainelPayload) =>
      "a linha da fila de " +
      useRntrcFilaService()
        .getNomeFila(l.fila)
        .toLowerCase() +
      (l.idRntrcSolicitacao
        ? " da solicitação nº " + l.idRntrcSolicitacao
        : "");

    const reprocessar = (l: RntrcFilaPainelPayload) => {
      useConfirm().require({
        message: "Reprocessar " + descricaoLinha(l) + "?",
        header: "Reprocessar",
        icon: "pi pi-info-circle",
        accept: () => {
          const { loading, reprocessar } = useRntrcFilaService();
          state.loadings.push(loading);
          reprocessar(l.fila, l.idFila)
            .then(() => {
              removeLoading(loading);
              useToast().success("Linha recolocada na fila.");
              submitSearch();
            })
            .catch(() => removeLoading(loading));
        }
      });
    };

    const remover = (l: RntrcFilaPainelPayload) => {
      useConfirm().require({
        message:
          "Remover " +
          descricaoLinha(l) +
          "? O robô deixa de processá-la; a solicitação não muda de situação.",
        header: "Remover da fila",
        icon: "pi pi-exclamation-triangle",
        acceptClass: "p-button-danger",
        accept: () => {
          const { loading, remover } = useRntrcFilaService();
          state.loadings.push(loading);
          remover(l.fila, l.idFila)
            .then(() => {
              removeLoading(loading);
              useToast().success("Linha removida da fila.");
              submitSearch();
            })
            .catch(() => removeLoading(loading));
        }
      });
    };

    const resumir = (texto?: string | null) => {
      if (!texto) {
        return "";
      }
      return texto.length > 80 ? texto.substring(0, 80) + "…" : texto;
    };

    onMounted(() => {
      submitSearch();
    });

    return {
      stateLoading,
      submitSearch,
      reprocessar,
      remover,
      resumir,
      getNomeFila: useRntrcFilaService().getNomeFila,
      formatDateTime,
      formatCpfCnpj,
      getSituacaoCor,
      getSituacaoLabel,
      getTipoServicoLabel,
      ...toRefs(state)
    };
  }
});
